/**
 * Return-path allow-listing for the sign-in round trip.
 *
 * After a redirect to the hosted UI the console sends the user back to wherever
 * they were headed. That destination is attacker-influenceable in principle (it
 * reaches us through router state or a query string), so it is treated as
 * untrusted input and reduced to a *same-origin relative path* before anything
 * navigates to it. Anything else collapses to the console root rather than
 * being repaired — a wrong-but-safe landing beats a clever redirect.
 *
 * The rules are deliberately narrow:
 *   - must start with exactly one "/" (rejects "//host", which a browser reads
 *     as protocol-relative and resolves off-origin)
 *   - must not contain a scheme, "\" (which some parsers fold to "/"), or a
 *     control character
 *   - keeps at most one "?" query and one "#" fragment
 */

export const CONSOLE_ROOT = "/";

/** True when `candidate` is a path this console may navigate to after sign-in. */
export function isSafeReturnPath(
  candidate: string | null | undefined,
): boolean {
  if (typeof candidate !== "string" || candidate.length === 0) return false;
  if (candidate.length > 2048) return false;
  if (!candidate.startsWith("/")) return false;
  // "//evil.example" and "/\evil.example" are both off-origin once a browser
  // normalises them, even though each starts with a single visible slash.
  if (candidate.startsWith("//") || candidate.startsWith("/\\")) return false;
  if (candidate.includes("\\")) return false;
  // Control characters can split a URL for a downstream parser that disagrees
  // with ours; a legitimate console route never contains one.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(candidate)) return false;
  // A scheme cannot appear in a path-only value.
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(candidate)) return false;
  return true;
}

/**
 * Reduce an untrusted destination to a navigable console path.
 *
 * Returns {@link CONSOLE_ROOT} for anything that fails {@link isSafeReturnPath},
 * so callers can use the result unconditionally.
 */
export function safeReturnPath(candidate: string | null | undefined): string {
  return isSafeReturnPath(candidate) ? (candidate as string) : CONSOLE_ROOT;
}
